import { Suspense, useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import type { HomeworkCard } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { LazyHomeworkFeed, LazyHomeworkResults } from "../exercises/lazy.ts";
import { useHomeworkNewsRefresh } from "../shell/HomeworkNews.tsx";
import { Crumbs } from "../shell/Crumbs.tsx";
import { Loading } from "../../lib/loading.tsx";

/**
 * Карточка домашки: тонкая обвязка из стартового чанка.
 *
 * Сама лента (ученик) и экран результатов (преподаватель) живут в ленивом
 * чанке упражнений: за ними едут `@edu/shared/checking` и KaTeX, а стартовый
 * экран на 128 кбит/с обязан открыться раньше них (правило 1.3 проекта
 * choice-exercise-design.md).
 *
 * Роль решает сервер (`myRole` в карточке), клиент её только читает.
 *
 * ⚠️ Экран помнит, с какого адреса на него пришли, и возвращается ИМЕННО туда:
 * разрез по ученику (`?studentId=`) обязан пережить и крошку, и уход после «В
 * корзину». Иначе преподаватель, проверяющий двенадцать работ подряд,
 * двенадцать раз выбирает ученика заново (5.1 и 5.10 фазы 2).
 */

/** Адрес хаба, с которого пришли. Прямой заход даёт общий список. */
function backToOf(raw: unknown): string {
  if (typeof raw !== "object" || raw === null) return "/homework";
  const value = (raw as { from?: unknown }).from;
  // Только наш собственный адрес: строка из истории это не то, чему стоит верить
  return typeof value === "string" && value.startsWith("/homework") ? value : "/homework";
}

export function HomeworkPage() {
  const { homeworkId = "" } = useParams();
  const { state: sessionState } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = backToOf(location.state);
  const [card, setCard] = useState<HomeworkCard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  /**
   * Просьба каркасу перечесть «сколько работ не открыто».
   *
   * ⚠️ Зовётся ПОСЛЕ ответа карточки, и в этом весь смысл: отметку «ученик
   * открыл» ставит сам этот запрос, а перечитать счёт до его ответа значит
   * посчитать до отметки. Разбор в шапке `HomeworkNews.tsx`.
   *
   * Роль не проверяется: у преподавателя счёт всегда ноль, и лишний `count(*)`
   * по индексу дешевле, чем второе место, знающее правило про роли.
   */
  const refreshNews = useHomeworkNewsRefresh();

  /**
   * Возвращает свежую карточку: подписанные ссылки на картинки материала
   * живут минуты, и лента перечитывает карточку, чтобы взять новые
   * (1.3 docs/homework-scratch-design.md).
   */
  const reload = useCallback(async (): Promise<HomeworkCard | null> => {
    try {
      const fresh = await api.getHomework(homeworkId);
      setCard(fresh);
      setErr(null);
      refreshNews();
      return fresh;
    } catch (e) {
      if (e instanceof api.ApiError && e.status === 404) {
        // Чужая и несуществующая одинаково 404: сервер отвечает так намеренно
        setErr("Домашка не найдена. Возможно, её удалил преподаватель.");
      } else {
        // Перезагрузка офлайн: данных заданий в браузере нет, честное «нет
        // связи» вместо пустоты (9.3 проекта). Повтор по кнопке и по online
        setErr("Нет связи. Проверьте интернет и попробуйте ещё раз.");
      }
      return null;
    }
  }, [homeworkId, refreshNews]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Повтор сам по возврату сети, не только кнопкой
  useEffect(() => {
    const wake = (): void => {
      void reload();
    };
    window.addEventListener("online", wake);
    return () => window.removeEventListener("online", wake);
  }, [reload]);

  if (err !== null && card === null) {
    return (
      <main className="home home--list">
        <Head title={null} backTo={backTo} />
        <p className="err">{err}</p>
        <p className="acts">
          <button className="btn" onClick={() => void reload()}>Повторить</button>
          <Link className="btn" to={backTo}>
            К списку
          </Link>
        </p>
      </main>
    );
  }

  if (card === null || sessionState.status !== "signed") {
    return (
      <main className="home home--list">
        <Head title={null} backTo={backTo} />
        <Loading />
      </main>
    );
  }

  return (
    <main className="home home--list">
      <Head title={card.title} backTo={backTo} />
      <Suspense fallback={<Loading />}>
        {card.myRole === "teacher" ? (
          <LazyHomeworkResults
            card={card}
            onChanged={() => void reload()}
            onRefresh={reload}
            backTo={backTo}
            /*
              ⚠️ Уход в список, а не перечитывание карточки. Правило доступа не
              отдаёт удалённую работу с нуждой `view`, поэтому перечитывание
              молча не меняло на экране НИЧЕГО: отказ ложился в состояние, а
              рисуется он только при пустой карточке. Работа при этом уже была в
              корзине. Уходим на тот адрес, с которого пришли, вместе с разрезом,
              и просим раскрыть корзину: иначе работа не видна нигде (5.10 фазы 2)
            */
            onTrashed={() => void navigate(backTo, { state: { trashedId: card.id } })}
          />
        ) : (
          <LazyHomeworkFeed card={card} userId={sessionState.me.userId} onRefresh={reload} />
        )}
      </Suspense>
    </main>
  );
}

/**
 * Шапка карточки работы.
 *
 * ⚠️ **Долг этапа 3, закрыт 14.09.2026.** До этого дня это был единственный
 * экран кабинета БЕЗ шапки `.head`: сверху стоял абзац `.muted` с косой чертой,
 * то есть пятая трактовка крошек, а заголовок жил этажом ниже, в ленивом чанке
 * упражнений (`features/exercises/HomeworkFeed.tsx` и `HomeworkResults.tsx`,
 * узел `.hwfeed__head`). Экран из-за этого читался как другая программа: у
 * соседей антиква на 28 px с линейкой под ней, здесь полужирный на 20 px внутри
 * ленты.
 *
 * **Заголовок переехал сюда, подпись осталась внизу**, и делится это ровно по
 * тому, откуда берутся данные. Название работы приходит вместе с карточкой и
 * одинаково у обеих сторон. Подпись у них разная и СЧИТАЕТСЯ: у преподавателя из
 * счётчиков ответа сервера, у ученика из живых черновиков ленивого чанка, то
 * есть она меняется, пока человек отвечает. Затащив её наверх, мы бы либо
 * протянули состояние ленты через `Suspense` вверх, либо показали бы устаревшее
 * число в шапке.
 *
 * Крошки общим компонентом, а не своей копией: разделитель шевроном, последний
 * шаг не ссылка, читалке путь назван словом. Адрес возврата несёт разрез
 * списка, с которого пришли.
 *
 * Главного действия у экрана нет: всё, что тут делают, делают с конкретным
 * заданием или со всей работой внизу («Показать разбор», «В корзину»), и ни
 * одно из этого не главное действие экрана. Место справа стоит пустым.
 */
function Head({ title, backTo }: { title: string | null; backTo: string }) {
  return (
    <header className="head">
      <Crumbs trail={[{ label: "Домашка", to: backTo }]} here={title ?? "…"} />
      <div className="head__line">
        <h1 className="head__h">{title ?? "…"}</h1>
      </div>
    </header>
  );
}
